import React from "react";
import { useInternalAffairsEnabled } from "@/pages/StationAdmin/featureFlag";
import NotFound404 from "@/pages/NotFound";

/**
 * Route guard for the Internal Affairs tab, gated by the per-station WorkOS
 * `internal-affairs` flag (see useInternalAffairsEnabled) — separate from the
 * `admin-dashboard` flag that gates the Overview dashboard. This nests INSIDE
 * RequireStationAdminArea + RequireRole in App.tsx, so reaching Internal
 * Affairs requires the station_admin role and this flag; it does NOT require
 * `admin-dashboard` (a station can have IA without the dashboard). Toggling
 * `internal-affairs` turns the tab on or off independently.
 *
 * Deliberately a guard rather than a conditional `<Route>`. WorkOS feature
 * flags aren't populated until the session finishes restoring, so on a hard
 * refresh `useInternalAffairsEnabled()` is briefly false. A route that is only
 * *registered* when the flag is on would therefore be absent during that
 * window, and the router would fall through to the catch-all 404 — flashing
 * the not-found page for a moment before the real page mounts. Keeping the
 * path always registered means the URL instead resolves to the loading spinner
 * while auth restores; only once loading has cleared (and the flag is genuinely
 * off) does a disabled station see the 404 — same pattern as
 * RequireStationAdminArea and RequireIncidentManagement.
 */
export function RequireInternalAffairs({
  children,
}: {
  children: React.ReactNode;
}) {
  const enabled = useInternalAffairsEnabled();
  if (!enabled) return <NotFound404 />;
  return <>{children}</>;
}
